"use client";

import { Component } from "react";

import { Text } from "@/app/components/client";

interface ErrorBoundaryProps {
	children?: React.ReactNode;
	componentId?: string;
	errorComponent?: JSX.Element;
}

interface ErrorBoundaryState {
	error?: Error & { digest?: string };
	hasError: boolean;
}

const tableBorder = "1px solid #888";
const tableCellsPadding = "8px 16px";

export class ErrorBoundary extends Component<
	ErrorBoundaryProps,
	ErrorBoundaryState
> {
	state: ErrorBoundaryState = {
		error: undefined,
		hasError: false
	};

	static getDerivedStateFromError(
		error: Error & { digest?: string }
	): ErrorBoundaryState {
		return { error, hasError: true };
	}

	componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
		console.error("Uncaught error:", error, errorInfo);
	}

	render(): React.ReactNode {
		if (!this.state.hasError) return this.props.children;
		if (this.props.errorComponent) return this.props.errorComponent;
		// Shoppers must never see the stack dump below.
		if (process.env.NODE_ENV === "production") return null;

		return (
			<details style={{ padding: "32px" }}>
				<summary>
					<Text
						tag="span"
						styledAs="mdb"
						style={{ color: "red", paddingLeft: 8 }}
					>
						Component{` ${this.props.componentId}` ?? ""} had an
						error
					</Text>
				</summary>

				<table
					style={{
						display: "inline-block",
						border: tableBorder,
						marginTop: "12px",
						overflowX: "auto"
					}}
				>
					<tbody>
						{Object.entries({
							"Error name": this.state.error?.name,
							"Error message": this.state.error?.message,
							"Error stack": this.state.error?.stack,
							"Error digest": this.state.error?.digest || "-"
						}).map(([key, entry]) => (
							<tr key={key} style={{ borderBottom: tableBorder }}>
								<td
									style={{
										padding: tableCellsPadding,
										verticalAlign: "top"
									}}
								>
									<b>{key}</b>
								</td>

								<td
									style={{
										padding: tableCellsPadding,
										borderLeft: tableBorder,
										whiteSpace: "pre"
									}}
								>
									{entry}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</details>
		);
	}
}
